import { t } from "../../i18n";
import { ExternalLink, Link2, Search } from "lucide-react";
import { asRecord, domain, stringValue } from "./ToolResultView.utils";
import { EmptyResult } from "../EmptyResult";
import { CopyButton } from "../CopyButton";

export function SearchResult({
  result,
  query,
}: {
  result: unknown;
  query?: string;
}) {
  const values = asRecord(result);
  const results = Array.isArray(values?.results) ? values.results : [];
  if (!results.length)
    return <EmptyResult icon={<Search size={16} />} text="No results found" />;
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2 rounded-xl border border-lumen-200 bg-lumen-50/60 p-3 dark:border-lumen-900/60 dark:bg-lumen-950/20">
        <Search
          size={15}
          className="mt-0.5 shrink-0 text-lumen-700 dark:text-lumen-300"
        />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-stone-500 dark:text-stone-300">
            {t("Search results")}
          </div>
          {query && (
            <div className="mt-0.5 truncate text-sm font-semibold text-stone-800 dark:text-stone-100">
              {query}
            </div>
          )}
        </div>
        <span className="shrink-0 rounded-full bg-white/80 px-2 py-1 text-[10px] font-semibold text-lumen-700 dark:bg-white/10 dark:text-lumen-300">
          {t("{{count}} results", { count: results.length })}
        </span>
      </div>
      <div className="grid gap-2">
        {results.map((item, index) => {
          const entry = asRecord(item);
          const url = stringValue(entry?.url);
          const title =
            stringValue(entry?.title) || url || `Result ${index + 1}`;
          return (
            <div
              key={`${url}-${index}`}
              className="group block max-w-full min-w-0 overflow-hidden rounded-xl border border-stone-200 bg-white/80 p-3 transition hover:border-lumen-400 hover:shadow-sm dark:border-white/10 dark:bg-[#171b1a]"
            >
              <a
                href={url || undefined}
                target="_blank"
                rel="noreferrer"
                className="flex min-w-0 items-start gap-2"
              >
                <div className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-md bg-lumen-50 text-lumen-700 dark:bg-lumen-900/30 dark:text-lumen-300">
                  <Link2 size={13} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-stone-800 group-hover:text-lumen-700 dark:text-stone-100 dark:group-hover:text-lumen-300">
                      {title}
                    </span>
                    {url && (
                      <ExternalLink
                        size={12}
                        className="shrink-0 text-stone-400"
                      />
                    )}
                  </div>
                  {url && (
                    <div className="mt-0.5 max-w-full truncate text-[11px] text-lumen-700 dark:text-lumen-300">
                      {domain(url)}
                    </div>
                  )}
                  {stringValue(entry?.content) && (
                    <p className="mt-1.5 line-clamp-2 text-xs leading-5 break-words text-stone-500 dark:text-stone-400">
                      {stringValue(entry?.content)}
                    </p>
                  )}
                </div>
              </a>
              {url && (
                <div className="mt-2 flex justify-end">
                  <CopyButton value={url} label={t("Copy link")} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
